/**
 * Mock Projects Data
 *
 * Rich demo data that mirrors what would come from the Projects application API.
 * Includes all fields needed by the Projects page: status, type, code, name,
 * customer, project manager, dates, template, priority, team, milestones and budget.
 */

export type ProjectStatus = 'planning' | 'in progress' | 'on hold' | 'cancelled' | 'completed';
export type ProjectType = 'fixed-price' | 'time-materials' | 'retainer';
export type ProjectPriority = 'critical' | 'high' | 'medium' | 'low';

export interface ProjectTeamMember {
  id: string;
  name: string;
  role: string;
  department: string;
  email: string;
  avatarUrl?: string;
  allocation: number; // percentage
  hoursAllocated: number;
  hoursLogged: number;
}

export interface ProjectMilestone {
  id: string;
  name: string;
  dueDate: string;
  completedDate?: string;
  status: 'pending' | 'in-progress' | 'completed' | 'overdue';
  description?: string;
}

export interface ProjectBudgetItem {
  id: string;
  resource: string;
  role: string;
  budgetedHours: number;
  actualHours: number;
  remainingHours: number;
  costRate: number;
  billingRate: number;
}

export interface ProjectTimeEntry {
  id: string;
  date: string;
  resource: string;
  description: string;
  hours: number;
  billable: boolean;
  status: 'pending' | 'approved' | 'rejected';
}

export interface DemoProject {
  id: string;
  code: string;
  name: string;
  description: string;
  status: ProjectStatus;
  type: ProjectType;
  priority: ProjectPriority;
  template: string;
  // Customer
  customerCode?: string;
  customerName?: string;
  // People
  projectManagerId: string;
  projectManagerName: string;
  projectManagerAvatar?: string;
  // Dates
  startDate: string;
  endDate: string;
  // Progress
  percentComplete: number;
  // Budget
  budgetedHours: number;
  actualHours: number;
  budgetedValue: number;
  actualCost: number;
  // Deep link
  projectUrl: string;
  // Related data
  team: ProjectTeamMember[];
  milestones: ProjectMilestone[];
  budgetItems: ProjectBudgetItem[];
  timeEntries: ProjectTimeEntry[];
  // Extra
  tags: string[];
  region: string;
}

// ─── Status config ─────────────────────────────────────────────────────────

export const PROJECT_STATUS_CONFIG: Record<
  ProjectStatus,
  { label: string; accent: string; dotClass: string }
> = {
  'planning': {
    label: 'Planning',
    accent: 'info',
    dotClass: 'bg-blue-500',
  },
  'in progress': {
    label: 'In Progress',
    accent: 'success',
    dotClass: 'bg-green-500',
  },
  'on hold': {
    label: 'On Hold',
    accent: 'warning',
    dotClass: 'bg-amber-500',
  },
  'cancelled': {
    label: 'Cancelled',
    accent: 'destructive',
    dotClass: 'bg-red-500',
  },
  'completed': {
    label: 'Completed',
    accent: 'teal',
    dotClass: 'bg-teal-500',
  },
};

export const PROJECT_TYPE_CONFIG: Record<
  ProjectType,
  { label: string; accent: string }
> = {
  'fixed-price': { label: 'Fixed Price', accent: 'purple' },
  'time-materials': { label: 'Time & Materials', accent: 'nile' },
  'retainer': { label: 'Retainer', accent: 'orange' },
};

export const PROJECT_PRIORITY_CONFIG: Record<
  ProjectPriority,
  { label: string; accent: string }
> = {
  'critical': { label: 'Critical', accent: 'destructive' },
  'high': { label: 'High', accent: 'warning' },
  'medium': { label: 'Medium', accent: 'info' },
  'low': { label: 'Low', accent: 'success' },
};

// ─── Mock Data ──────────────────────────────────────────────────────────────

export const mockProjects: DemoProject[] = [
  // 1
  {
    id: 'proj-001',
    code: 'UK-DIGITAL-2024',
    name: 'UK Digital Transformation',
    description:
      'End-to-end digital transformation initiative to modernise legacy systems and introduce cloud-native services across UK operations.',
    status: 'in progress',
    type: 'fixed-price',
    priority: 'critical',
    template: 'Digital Transformation',
    customerCode: 'BRITVIC-001',
    customerName: 'Britvic plc',
    projectManagerId: 'res-pm-001',
    projectManagerName: 'Sarah Mitchell',
    projectManagerAvatar: 'https://i.pravatar.cc/48?img=47',
    startDate: '2024-01-15',
    endDate: '2024-12-31',
    percentComplete: 62,
    budgetedHours: 4800,
    actualHours: 2976,
    budgetedValue: 960000,
    actualCost: 595200,
    projectUrl: 'https://projects.accessgroup.com/UK-DIGITAL-2024',
    tags: ['Digital', 'Cloud', 'Enterprise'],
    region: 'UK',
    team: [
      { id: 'tm-001', name: 'James Thornton', role: 'Lead Developer', department: 'Engineering', email: 'j.thornton@example.com', avatarUrl: 'https://i.pravatar.cc/80?img=11', allocation: 100, hoursAllocated: 1200, hoursLogged: 744 },
      { id: 'tm-002', name: 'Priya Sharma', role: 'UX Designer', department: 'Design', email: 'p.sharma@example.com', avatarUrl: 'https://i.pravatar.cc/80?img=5', allocation: 80, hoursAllocated: 800, hoursLogged: 496 },
      { id: 'tm-003', name: 'Mark Davies', role: 'Solution Architect', department: 'Architecture', email: 'm.davies@example.com', avatarUrl: 'https://i.pravatar.cc/80?img=15', allocation: 60, hoursAllocated: 600, hoursLogged: 372 },
      { id: 'tm-004', name: 'Lucy Chen', role: 'QA Engineer', department: 'Quality', email: 'l.chen@example.com', avatarUrl: 'https://i.pravatar.cc/80?img=9', allocation: 100, hoursAllocated: 900, hoursLogged: 558 },
      { id: 'tm-005', name: 'Tom Walsh', role: 'DevOps Engineer', department: 'Infrastructure', email: 't.walsh@example.com', avatarUrl: 'https://i.pravatar.cc/80?img=53', allocation: 50, hoursAllocated: 300, hoursLogged: 186 },
    ],
    milestones: [
      { id: 'ms-001', name: 'Discovery & Scoping', dueDate: '2024-02-28', completedDate: '2024-02-25', status: 'completed', description: 'Full as-is analysis and to-be design.' },
      { id: 'ms-002', name: 'Architecture Design', dueDate: '2024-04-30', completedDate: '2024-04-28', status: 'completed', description: 'Cloud architecture blueprints approved.' },
      { id: 'ms-003', name: 'Phase 1 Delivery', dueDate: '2024-07-31', completedDate: '2024-07-29', status: 'completed', description: 'Core services migrated to cloud.' },
      { id: 'ms-004', name: 'Phase 2 Delivery', dueDate: '2024-10-31', status: 'in-progress', description: 'User-facing applications modernised.' },
      { id: 'ms-005', name: 'Go-Live & Handover', dueDate: '2024-12-20', status: 'pending', description: 'Full production deployment and knowledge transfer.' },
    ],
    budgetItems: [
      { id: 'bi-001', resource: 'James Thornton', role: 'Lead Developer', budgetedHours: 1200, actualHours: 744, remainingHours: 456, costRate: 95, billingRate: 150 },
      { id: 'bi-002', resource: 'Priya Sharma', role: 'UX Designer', budgetedHours: 800, actualHours: 496, remainingHours: 304, costRate: 75, billingRate: 125 },
      { id: 'bi-003', resource: 'Mark Davies', role: 'Solution Architect', budgetedHours: 600, actualHours: 372, remainingHours: 228, costRate: 110, billingRate: 175 },
      { id: 'bi-004', resource: 'Lucy Chen', role: 'QA Engineer', budgetedHours: 900, actualHours: 558, remainingHours: 342, costRate: 70, billingRate: 115 },
      { id: 'bi-005', resource: 'Tom Walsh', role: 'DevOps Engineer', budgetedHours: 300, actualHours: 186, remainingHours: 114, costRate: 90, billingRate: 145 },
    ],
    timeEntries: [
      { id: 'te-001', date: '2024-11-04', resource: 'James Thornton', description: 'Phase 2 API development – authentication module', hours: 8, billable: true, status: 'approved' },
      { id: 'te-002', date: '2024-11-04', resource: 'Priya Sharma', description: 'UX review of new dashboard components', hours: 6, billable: true, status: 'approved' },
      { id: 'te-003', date: '2024-11-05', resource: 'Mark Davies', description: 'Architecture review and sign-off', hours: 4, billable: true, status: 'pending' },
      { id: 'te-004', date: '2024-11-05', resource: 'Lucy Chen', description: 'Regression testing sprint 8', hours: 8, billable: true, status: 'approved' },
      { id: 'te-005', date: '2024-11-06', resource: 'Tom Walsh', description: 'CI/CD pipeline optimisation', hours: 7, billable: true, status: 'pending' },
    ],
  },
  // 2
  {
    id: 'proj-002',
    code: 'USA-ERP-2024',
    name: 'USA Enterprise System',
    description:
      'Enterprise Resource Planning implementation for the North American operations covering finance, HR and procurement modules.',
    status: 'in progress',
    type: 'time-materials',
    priority: 'high',
    template: 'ERP Implementation',
    customerCode: 'HILTON-002',
    customerName: 'Hilton Hotels & Resorts',
    projectManagerId: 'res-pm-002',
    projectManagerName: 'David Nguyen',
    projectManagerAvatar: 'https://i.pravatar.cc/48?img=12',
    startDate: '2024-03-01',
    endDate: '2025-02-28',
    percentComplete: 38,
    budgetedHours: 6400,
    actualHours: 2432,
    budgetedValue: 1280000,
    actualCost: 486400,
    projectUrl: 'https://projects.accessgroup.com/USA-ERP-2024',
    tags: ['ERP', 'Finance', 'HR'],
    region: 'North America',
    team: [
      { id: 'tm-006', name: 'Rachel Kim', role: 'ERP Consultant', department: 'Consulting', email: 'r.kim@example.com', avatarUrl: 'https://i.pravatar.cc/80?img=23', allocation: 100, hoursAllocated: 2000, hoursLogged: 760 },
      { id: 'tm-007', name: 'Carlos Mendez', role: 'Finance Module Lead', department: 'Finance', email: 'c.mendez@example.com', avatarUrl: 'https://i.pravatar.cc/80?img=60', allocation: 80, hoursAllocated: 1600, hoursLogged: 608 },
      { id: 'tm-008', name: 'Anna Petrov', role: 'Change Manager', department: 'Change', email: 'a.petrov@example.com', avatarUrl: 'https://i.pravatar.cc/80?img=47', allocation: 60, hoursAllocated: 800, hoursLogged: 304 },
    ],
    milestones: [
      { id: 'ms-006', name: 'Requirements Gathering', dueDate: '2024-04-30', completedDate: '2024-04-28', status: 'completed', description: 'Full requirements sign-off from all stakeholders.' },
      { id: 'ms-007', name: 'Finance Module Go-Live', dueDate: '2024-08-31', status: 'in-progress', description: 'Finance and procurement modules live.' },
      { id: 'ms-008', name: 'HR Module Go-Live', dueDate: '2024-11-30', status: 'pending', description: 'HR and payroll modules live.' },
      { id: 'ms-009', name: 'Full System Go-Live', dueDate: '2025-02-15', status: 'pending', description: 'All modules live across all regions.' },
    ],
    budgetItems: [
      { id: 'bi-006', resource: 'Rachel Kim', role: 'ERP Consultant', budgetedHours: 2000, actualHours: 760, remainingHours: 1240, costRate: 105, billingRate: 170 },
      { id: 'bi-007', resource: 'Carlos Mendez', role: 'Finance Module Lead', budgetedHours: 1600, actualHours: 608, remainingHours: 992, costRate: 95, billingRate: 155 },
      { id: 'bi-008', resource: 'Anna Petrov', role: 'Change Manager', budgetedHours: 800, actualHours: 304, remainingHours: 496, costRate: 80, billingRate: 130 },
    ],
    timeEntries: [
      { id: 'te-006', date: '2024-11-04', resource: 'Rachel Kim', description: 'Finance module configuration – chart of accounts', hours: 8, billable: true, status: 'approved' },
      { id: 'te-007', date: '2024-11-05', resource: 'Carlos Mendez', description: 'AP/AR workflow setup and testing', hours: 7, billable: true, status: 'approved' },
      { id: 'te-008', date: '2024-11-06', resource: 'Anna Petrov', description: 'Change management workshop – finance team', hours: 5, billable: true, status: 'pending' },
    ],
  },
  // 3
  {
    id: 'proj-003',
    code: 'DE-INDUSTRY-2024',
    name: 'Germany Industry 4.0',
    description:
      'Industrial automation and IoT integration programme for the German manufacturing plants, enabling real-time production monitoring.',
    status: 'planning',
    type: 'fixed-price',
    priority: 'critical',
    template: 'Industrial IoT',
    customerCode: 'SIEMENS-003',
    customerName: 'Siemens AG',
    projectManagerId: 'res-pm-003',
    projectManagerName: 'Klaus Weber',
    projectManagerAvatar: 'https://i.pravatar.cc/48?img=33',
    startDate: '2024-11-01',
    endDate: '2025-10-31',
    percentComplete: 5,
    budgetedHours: 8000,
    actualHours: 400,
    budgetedValue: 2000000,
    actualCost: 100000,
    projectUrl: 'https://projects.accessgroup.com/DE-INDUSTRY-2024',
    tags: ['IoT', 'Manufacturing', 'Automation'],
    region: 'Europe',
    team: [
      { id: 'tm-009', name: 'Hans Müller', role: 'IoT Architect', department: 'Engineering', email: 'h.muller@example.com', avatarUrl: 'https://i.pravatar.cc/80?img=57', allocation: 100, hoursAllocated: 2400, hoursLogged: 120 },
      { id: 'tm-010', name: 'Eva Braun', role: 'Embedded Systems Engineer', department: 'Hardware', email: 'e.braun@example.com', avatarUrl: 'https://i.pravatar.cc/80?img=16', allocation: 100, hoursAllocated: 2400, hoursLogged: 120 },
      { id: 'tm-011', name: 'Stefan Koch', role: 'Data Scientist', department: 'Analytics', email: 's.koch@example.com', avatarUrl: 'https://i.pravatar.cc/80?img=69', allocation: 80, hoursAllocated: 1600, hoursLogged: 80 },
    ],
    milestones: [
      { id: 'ms-010', name: 'Project Kickoff', dueDate: '2024-11-15', status: 'completed', completedDate: '2024-11-14', description: 'Initial kickoff with client stakeholders.' },
      { id: 'ms-011', name: 'Proof of Concept', dueDate: '2025-01-31', status: 'pending', description: 'IoT sensor pilot on Plant 1.' },
      { id: 'ms-012', name: 'Phase 1 – Plant 1', dueDate: '2025-04-30', status: 'pending', description: 'Full IoT deployment on Plant 1.' },
      { id: 'ms-013', name: 'Phase 2 – Plants 2 & 3', dueDate: '2025-07-31', status: 'pending', description: 'Roll-out to Plants 2 and 3.' },
      { id: 'ms-014', name: 'Final Delivery', dueDate: '2025-10-15', status: 'pending', description: 'All plants live, handover documentation complete.' },
    ],
    budgetItems: [
      { id: 'bi-009', resource: 'Hans Müller', role: 'IoT Architect', budgetedHours: 2400, actualHours: 120, remainingHours: 2280, costRate: 120, billingRate: 195 },
      { id: 'bi-010', resource: 'Eva Braun', role: 'Embedded Systems Engineer', budgetedHours: 2400, actualHours: 120, remainingHours: 2280, costRate: 110, billingRate: 180 },
      { id: 'bi-011', resource: 'Stefan Koch', role: 'Data Scientist', budgetedHours: 1600, actualHours: 80, remainingHours: 1520, costRate: 100, billingRate: 165 },
    ],
    timeEntries: [
      { id: 'te-009', date: '2024-11-04', resource: 'Hans Müller', description: 'Requirements workshops and architecture planning', hours: 8, billable: true, status: 'approved' },
      { id: 'te-010', date: '2024-11-05', resource: 'Eva Braun', description: 'Hardware selection and BoM preparation', hours: 8, billable: true, status: 'approved' },
    ],
  },
  // 4
  {
    id: 'proj-004',
    code: 'AU-MINING-2024',
    name: 'Australia Mining Tech',
    description:
      'Implementation of a real-time mining operations platform integrating fleet management, safety monitoring and production forecasting.',
    status: 'in progress',
    type: 'time-materials',
    priority: 'high',
    template: 'Operations Platform',
    customerCode: 'BHPBIL-004',
    customerName: 'BHP Billiton Ltd',
    projectManagerId: 'res-pm-004',
    projectManagerName: 'Emma Clarke',
    projectManagerAvatar: 'https://i.pravatar.cc/48?img=25',
    startDate: '2024-02-01',
    endDate: '2024-11-30',
    percentComplete: 88,
    budgetedHours: 3600,
    actualHours: 3168,
    budgetedValue: 720000,
    actualCost: 633600,
    projectUrl: 'https://projects.accessgroup.com/AU-MINING-2024',
    tags: ['Mining', 'Fleet', 'Safety'],
    region: 'Asia Pacific',
    team: [
      { id: 'tm-012', name: 'Liam O\'Brien', role: 'Platform Engineer', department: 'Engineering', email: 'l.obrien@example.com', avatarUrl: 'https://i.pravatar.cc/80?img=67', allocation: 100, hoursAllocated: 1400, hoursLogged: 1232 },
      { id: 'tm-013', name: 'Mei Zhang', role: 'Mobile Developer', department: 'Mobile', email: 'm.zhang@example.com', avatarUrl: 'https://i.pravatar.cc/80?img=10', allocation: 100, hoursAllocated: 1200, hoursLogged: 1056 },
      { id: 'tm-014', name: 'Jack Nguyen', role: 'Integration Specialist', department: 'Integration', email: 'j.nguyen@example.com', avatarUrl: 'https://i.pravatar.cc/80?img=63', allocation: 80, hoursAllocated: 600, hoursLogged: 528 },
    ],
    milestones: [
      { id: 'ms-015', name: 'Fleet Module', dueDate: '2024-05-31', completedDate: '2024-05-28', status: 'completed', description: 'Fleet tracking and dispatch live.' },
      { id: 'ms-016', name: 'Safety Module', dueDate: '2024-08-31', completedDate: '2024-08-29', status: 'completed', description: 'Safety monitoring and incident reporting live.' },
      { id: 'ms-017', name: 'Production Forecasting', dueDate: '2024-10-31', status: 'in-progress', description: 'Predictive production analytics dashboard.' },
      { id: 'ms-018', name: 'Project Close', dueDate: '2024-11-28', status: 'pending', description: 'Final UAT sign-off and project closure.' },
    ],
    budgetItems: [
      { id: 'bi-012', resource: 'Liam O\'Brien', role: 'Platform Engineer', budgetedHours: 1400, actualHours: 1232, remainingHours: 168, costRate: 90, billingRate: 145 },
      { id: 'bi-013', resource: 'Mei Zhang', role: 'Mobile Developer', budgetedHours: 1200, actualHours: 1056, remainingHours: 144, costRate: 85, billingRate: 140 },
      { id: 'bi-014', resource: 'Jack Nguyen', role: 'Integration Specialist', budgetedHours: 600, actualHours: 528, remainingHours: 72, costRate: 80, billingRate: 130 },
    ],
    timeEntries: [
      { id: 'te-011', date: '2024-11-04', resource: 'Liam O\'Brien', description: 'Production forecasting – ML model integration', hours: 8, billable: true, status: 'approved' },
      { id: 'te-012', date: '2024-11-05', resource: 'Mei Zhang', description: 'Mobile app final testing – iOS and Android', hours: 7, billable: true, status: 'approved' },
      { id: 'te-013', date: '2024-11-06', resource: 'Jack Nguyen', description: 'SAP integration final validation', hours: 6, billable: true, status: 'pending' },
    ],
  },
  // 5
  {
    id: 'proj-005',
    code: 'JP-ROBOTICS-2024',
    name: 'Japan Robotics Initiative',
    description:
      'Design and deployment of collaborative robotic systems for the Japanese automotive supply chain.',
    status: 'on hold',
    type: 'fixed-price',
    priority: 'medium',
    template: 'Robotics Programme',
    customerCode: 'TOYOTA-005',
    customerName: 'Toyota Motor Corporation',
    projectManagerId: 'res-pm-005',
    projectManagerName: 'Yuki Tanaka',
    projectManagerAvatar: 'https://i.pravatar.cc/48?img=56',
    startDate: '2024-04-01',
    endDate: '2025-03-31',
    percentComplete: 22,
    budgetedHours: 5200,
    actualHours: 1144,
    budgetedValue: 1560000,
    actualCost: 343200,
    projectUrl: 'https://projects.accessgroup.com/JP-ROBOTICS-2024',
    tags: ['Robotics', 'Automotive', 'AI'],
    region: 'Asia Pacific',
    team: [
      { id: 'tm-015', name: 'Kenji Ito', role: 'Robotics Engineer', department: 'Hardware', email: 'k.ito@example.com', avatarUrl: 'https://i.pravatar.cc/80?img=61', allocation: 100, hoursAllocated: 2000, hoursLogged: 440 },
      { id: 'tm-016', name: 'Aiko Yamamoto', role: 'AI/ML Engineer', department: 'AI', email: 'a.yamamoto@example.com', avatarUrl: 'https://i.pravatar.cc/80?img=49', allocation: 80, hoursAllocated: 1600, hoursLogged: 352 },
      { id: 'tm-017', name: 'Hiroshi Sato', role: 'Safety Engineer', department: 'Safety', email: 'h.sato@example.com', avatarUrl: 'https://i.pravatar.cc/80?img=68', allocation: 60, hoursAllocated: 900, hoursLogged: 198 },
    ],
    milestones: [
      { id: 'ms-019', name: 'Concept Design', dueDate: '2024-05-31', completedDate: '2024-05-30', status: 'completed', description: 'Robot arm concept designs approved.' },
      { id: 'ms-020', name: 'Prototype Build', dueDate: '2024-08-31', status: 'overdue', description: 'First working prototype – delayed due to component shortage.' },
      { id: 'ms-021', name: 'Pilot Line', dueDate: '2024-12-31', status: 'pending', description: 'Pilot robotic line operational.' },
      { id: 'ms-022', name: 'Full Rollout', dueDate: '2025-03-15', status: 'pending', description: 'All production lines equipped.' },
    ],
    budgetItems: [
      { id: 'bi-015', resource: 'Kenji Ito', role: 'Robotics Engineer', budgetedHours: 2000, actualHours: 440, remainingHours: 1560, costRate: 115, billingRate: 185 },
      { id: 'bi-016', resource: 'Aiko Yamamoto', role: 'AI/ML Engineer', budgetedHours: 1600, actualHours: 352, remainingHours: 1248, costRate: 120, billingRate: 195 },
      { id: 'bi-017', resource: 'Hiroshi Sato', role: 'Safety Engineer', budgetedHours: 900, actualHours: 198, remainingHours: 702, costRate: 95, billingRate: 155 },
    ],
    timeEntries: [
      { id: 'te-014', date: '2024-09-30', resource: 'Kenji Ito', description: 'Pre-hold – component procurement finalisation', hours: 4, billable: true, status: 'approved' },
    ],
  },
  // 6
  {
    id: 'proj-006',
    code: 'CA-ENERGY-2024',
    name: 'Canada Energy Solutions',
    description:
      'Renewable energy management system for monitoring and optimising wind and solar generation across Canadian provinces.',
    status: 'completed',
    type: 'fixed-price',
    priority: 'medium',
    template: 'Energy Platform',
    customerCode: 'EPCOR-006',
    customerName: 'EPCOR Utilities Inc.',
    projectManagerId: 'res-pm-006',
    projectManagerName: 'Sophie Tremblay',
    projectManagerAvatar: 'https://i.pravatar.cc/48?img=44',
    startDate: '2023-07-01',
    endDate: '2024-06-30',
    percentComplete: 100,
    budgetedHours: 3200,
    actualHours: 3040,
    budgetedValue: 640000,
    actualCost: 608000,
    projectUrl: 'https://projects.accessgroup.com/CA-ENERGY-2024',
    tags: ['Energy', 'Renewable', 'Analytics'],
    region: 'North America',
    team: [
      { id: 'tm-018', name: 'Pierre Leclerc', role: 'Systems Architect', department: 'Architecture', email: 'p.leclerc@example.com', avatarUrl: 'https://i.pravatar.cc/80?img=70', allocation: 100, hoursAllocated: 1200, hoursLogged: 1140 },
      { id: 'tm-019', name: 'Olivia Grant', role: 'Frontend Developer', department: 'Engineering', email: 'o.grant@example.com', avatarUrl: 'https://i.pravatar.cc/80?img=20', allocation: 100, hoursAllocated: 1000, hoursLogged: 950 },
      { id: 'tm-020', name: 'Ryan Scott', role: 'Data Engineer', department: 'Data', email: 'r.scott@example.com', avatarUrl: 'https://i.pravatar.cc/80?img=52', allocation: 80, hoursAllocated: 700, hoursLogged: 665 },
    ],
    milestones: [
      { id: 'ms-023', name: 'Platform Architecture', dueDate: '2023-09-30', completedDate: '2023-09-28', status: 'completed' },
      { id: 'ms-024', name: 'Wind Module', dueDate: '2023-12-31', completedDate: '2023-12-29', status: 'completed' },
      { id: 'ms-025', name: 'Solar Module', dueDate: '2024-03-31', completedDate: '2024-03-28', status: 'completed' },
      { id: 'ms-026', name: 'Go-Live & Sign-off', dueDate: '2024-06-30', completedDate: '2024-06-27', status: 'completed' },
    ],
    budgetItems: [
      { id: 'bi-018', resource: 'Pierre Leclerc', role: 'Systems Architect', budgetedHours: 1200, actualHours: 1140, remainingHours: 60, costRate: 105, billingRate: 170 },
      { id: 'bi-019', resource: 'Olivia Grant', role: 'Frontend Developer', budgetedHours: 1000, actualHours: 950, remainingHours: 50, costRate: 80, billingRate: 130 },
      { id: 'bi-020', resource: 'Ryan Scott', role: 'Data Engineer', budgetedHours: 700, actualHours: 665, remainingHours: 35, costRate: 90, billingRate: 145 },
    ],
    timeEntries: [
      { id: 'te-015', date: '2024-06-25', resource: 'Pierre Leclerc', description: 'Final architecture review and sign-off', hours: 6, billable: true, status: 'approved' },
      { id: 'te-016', date: '2024-06-26', resource: 'Olivia Grant', description: 'UI final polish and accessibility check', hours: 7, billable: true, status: 'approved' },
    ],
  },
  // 7
  {
    id: 'proj-007',
    code: 'FR-LUXURY-2024',
    name: 'France Luxury Platform',
    description:
      'Omnichannel e-commerce platform for a leading French luxury goods group, integrating boutique POS, online and clienteling.',
    status: 'in progress',
    type: 'retainer',
    priority: 'high',
    template: 'Retail Platform',
    customerCode: 'LVMH-007',
    customerName: 'LVMH Moët Hennessy',
    projectManagerId: 'res-pm-007',
    projectManagerName: 'Amélie Dubois',
    projectManagerAvatar: 'https://i.pravatar.cc/48?img=38',
    startDate: '2024-01-01',
    endDate: '2025-12-31',
    percentComplete: 47,
    budgetedHours: 9600,
    actualHours: 4512,
    budgetedValue: 1920000,
    actualCost: 902400,
    projectUrl: 'https://projects.accessgroup.com/FR-LUXURY-2024',
    tags: ['Retail', 'E-Commerce', 'Luxury'],
    region: 'Europe',
    team: [
      { id: 'tm-021', name: 'Chloé Martin', role: 'Product Owner', department: 'Product', email: 'c.martin@example.com', avatarUrl: 'https://i.pravatar.cc/80?img=32', allocation: 100, hoursAllocated: 2400, hoursLogged: 1128 },
      { id: 'tm-022', name: 'Antoine Girard', role: 'Backend Developer', department: 'Engineering', email: 'a.girard@example.com', avatarUrl: 'https://i.pravatar.cc/80?img=58', allocation: 100, hoursAllocated: 3200, hoursLogged: 1504 },
      { id: 'tm-023', name: 'Isabelle Moreau', role: 'Commerce Specialist', department: 'Commerce', email: 'i.moreau@example.com', avatarUrl: 'https://i.pravatar.cc/80?img=45', allocation: 80, hoursAllocated: 2000, hoursLogged: 940 },
      { id: 'tm-024', name: 'Nicolas Petit', role: 'Frontend Developer', department: 'Engineering', email: 'n.petit@example.com', avatarUrl: 'https://i.pravatar.cc/80?img=65', allocation: 60, hoursAllocated: 1000, hoursLogged: 470 },
    ],
    milestones: [
      { id: 'ms-027', name: 'Online Store v1', dueDate: '2024-04-30', completedDate: '2024-04-28', status: 'completed' },
      { id: 'ms-028', name: 'POS Integration', dueDate: '2024-08-31', completedDate: '2024-08-30', status: 'completed' },
      { id: 'ms-029', name: 'Clienteling Module', dueDate: '2024-12-31', status: 'in-progress' },
      { id: 'ms-030', name: 'International Markets', dueDate: '2025-06-30', status: 'pending' },
      { id: 'ms-031', name: 'Platform Optimisation', dueDate: '2025-12-15', status: 'pending' },
    ],
    budgetItems: [
      { id: 'bi-021', resource: 'Chloé Martin', role: 'Product Owner', budgetedHours: 2400, actualHours: 1128, remainingHours: 1272, costRate: 85, billingRate: 135 },
      { id: 'bi-022', resource: 'Antoine Girard', role: 'Backend Developer', budgetedHours: 3200, actualHours: 1504, remainingHours: 1696, costRate: 90, billingRate: 145 },
      { id: 'bi-023', resource: 'Isabelle Moreau', role: 'Commerce Specialist', budgetedHours: 2000, actualHours: 940, remainingHours: 1060, costRate: 95, billingRate: 155 },
      { id: 'bi-024', resource: 'Nicolas Petit', role: 'Frontend Developer', budgetedHours: 1000, actualHours: 470, remainingHours: 530, costRate: 80, billingRate: 130 },
    ],
    timeEntries: [
      { id: 'te-017', date: '2024-11-04', resource: 'Chloé Martin', description: 'Sprint 22 planning and backlog grooming', hours: 5, billable: true, status: 'approved' },
      { id: 'te-018', date: '2024-11-04', resource: 'Antoine Girard', description: 'Clienteling API – customer profile endpoints', hours: 8, billable: true, status: 'approved' },
      { id: 'te-019', date: '2024-11-05', resource: 'Isabelle Moreau', description: 'Commerce workflow UAT session', hours: 6, billable: true, status: 'pending' },
    ],
  },
  // 8
  {
    id: 'proj-008',
    code: 'UK-MOBILE-2024',
    name: 'UK Mobile Solutions',
    description:
      'Native mobile application suite for field service teams, enabling real-time job management, parts ordering and digital signatures.',
    status: 'cancelled',
    type: 'fixed-price',
    priority: 'low',
    template: 'Mobile App',
    customerCode: 'CENTRICA-008',
    customerName: 'Centrica plc',
    projectManagerId: 'res-pm-001',
    projectManagerName: 'Sarah Mitchell',
    projectManagerAvatar: 'https://i.pravatar.cc/48?img=47',
    startDate: '2024-05-01',
    endDate: '2024-10-31',
    percentComplete: 18,
    budgetedHours: 2400,
    actualHours: 432,
    budgetedValue: 480000,
    actualCost: 86400,
    projectUrl: 'https://projects.accessgroup.com/UK-MOBILE-2024',
    tags: ['Mobile', 'Field Service', 'iOS', 'Android'],
    region: 'UK',
    team: [
      { id: 'tm-025', name: 'Daniel Fisher', role: 'Mobile Lead', department: 'Mobile', email: 'd.fisher@example.com', avatarUrl: 'https://i.pravatar.cc/80?img=13', allocation: 100, hoursAllocated: 1200, hoursLogged: 216 },
      { id: 'tm-026', name: 'Fiona Campbell', role: 'UX Designer', department: 'Design', email: 'f.campbell@example.com', avatarUrl: 'https://i.pravatar.cc/80?img=29', allocation: 60, hoursAllocated: 600, hoursLogged: 108 },
    ],
    milestones: [
      { id: 'ms-032', name: 'UX Prototype', dueDate: '2024-06-30', completedDate: '2024-06-28', status: 'completed' },
      { id: 'ms-033', name: 'iOS Beta', dueDate: '2024-09-30', status: 'pending', description: 'Cancelled before this milestone.' },
    ],
    budgetItems: [
      { id: 'bi-025', resource: 'Daniel Fisher', role: 'Mobile Lead', budgetedHours: 1200, actualHours: 216, remainingHours: 0, costRate: 85, billingRate: 140 },
      { id: 'bi-026', resource: 'Fiona Campbell', role: 'UX Designer', budgetedHours: 600, actualHours: 108, remainingHours: 0, costRate: 75, billingRate: 120 },
    ],
    timeEntries: [
      { id: 'te-020', date: '2024-07-15', resource: 'Daniel Fisher', description: 'Development setup and repo configuration', hours: 8, billable: true, status: 'approved' },
    ],
  },
  // 9 – internal, no customer
  {
    id: 'proj-009',
    code: 'INT-PLATFORM-2024',
    name: 'Internal Platform Upgrade',
    description:
      'Internal tooling and platform upgrades to modernise the CI/CD pipeline, developer toolchain and internal monitoring systems.',
    status: 'in progress',
    type: 'time-materials',
    priority: 'medium',
    template: 'Infrastructure',
    projectManagerId: 'res-pm-002',
    projectManagerName: 'David Nguyen',
    projectManagerAvatar: 'https://i.pravatar.cc/48?img=12',
    startDate: '2024-07-01',
    endDate: '2024-12-31',
    percentComplete: 55,
    budgetedHours: 1600,
    actualHours: 880,
    budgetedValue: 0,
    actualCost: 88000,
    projectUrl: 'https://projects.accessgroup.com/INT-PLATFORM-2024',
    tags: ['Internal', 'DevOps', 'Infrastructure'],
    region: 'Global',
    team: [
      { id: 'tm-027', name: 'Tom Walsh', role: 'DevOps Engineer', department: 'Infrastructure', email: 't.walsh@example.com', avatarUrl: 'https://i.pravatar.cc/80?img=53', allocation: 60, hoursAllocated: 800, hoursLogged: 440 },
      { id: 'tm-028', name: 'Nadia Ali', role: 'Platform Engineer', department: 'Engineering', email: 'n.ali@example.com', avatarUrl: 'https://i.pravatar.cc/80?img=38', allocation: 50, hoursAllocated: 600, hoursLogged: 330 },
    ],
    milestones: [
      { id: 'ms-034', name: 'CI/CD Upgrade', dueDate: '2024-08-31', completedDate: '2024-08-29', status: 'completed' },
      { id: 'ms-035', name: 'Monitoring Stack', dueDate: '2024-10-31', status: 'in-progress' },
      { id: 'ms-036', name: 'Developer Portal', dueDate: '2024-12-20', status: 'pending' },
    ],
    budgetItems: [
      { id: 'bi-027', resource: 'Tom Walsh', role: 'DevOps Engineer', budgetedHours: 800, actualHours: 440, remainingHours: 360, costRate: 90, billingRate: 0 },
      { id: 'bi-028', resource: 'Nadia Ali', role: 'Platform Engineer', budgetedHours: 600, actualHours: 330, remainingHours: 270, costRate: 85, billingRate: 0 },
    ],
    timeEntries: [
      { id: 'te-021', date: '2024-11-04', resource: 'Tom Walsh', description: 'Grafana dashboard setup for monitoring', hours: 7, billable: false, status: 'approved' },
      { id: 'te-022', date: '2024-11-05', resource: 'Nadia Ali', description: 'Developer portal skeleton and routing', hours: 8, billable: false, status: 'approved' },
    ],
  },
  // 10
  {
    id: 'proj-010',
    code: 'USA-CRM-2024',
    name: 'USA Customer Platform',
    description:
      'Next-generation CRM and customer data platform to unify sales, marketing and service touchpoints across the US market.',
    status: 'planning',
    type: 'retainer',
    priority: 'high',
    template: 'CRM Implementation',
    customerCode: 'SALESFORCE-010',
    customerName: 'Salesforce Inc.',
    projectManagerId: 'res-pm-004',
    projectManagerName: 'Emma Clarke',
    projectManagerAvatar: 'https://i.pravatar.cc/48?img=25',
    startDate: '2025-01-06',
    endDate: '2025-12-31',
    percentComplete: 0,
    budgetedHours: 4800,
    actualHours: 0,
    budgetedValue: 1200000,
    actualCost: 0,
    projectUrl: 'https://projects.accessgroup.com/USA-CRM-2024',
    tags: ['CRM', 'Sales', 'Marketing'],
    region: 'North America',
    team: [
      { id: 'tm-029', name: 'Rachel Kim', role: 'CRM Consultant', department: 'Consulting', email: 'r.kim@example.com', avatarUrl: 'https://i.pravatar.cc/80?img=23', allocation: 100, hoursAllocated: 2000, hoursLogged: 0 },
      { id: 'tm-030', name: 'Carlos Mendez', role: 'Integration Lead', department: 'Integration', email: 'c.mendez@example.com', avatarUrl: 'https://i.pravatar.cc/80?img=60', allocation: 80, hoursAllocated: 1600, hoursLogged: 0 },
    ],
    milestones: [
      { id: 'ms-037', name: 'Discovery Workshop', dueDate: '2025-01-31', status: 'pending' },
      { id: 'ms-038', name: 'Solution Design', dueDate: '2025-03-31', status: 'pending' },
      { id: 'ms-039', name: 'Phase 1 Go-Live', dueDate: '2025-06-30', status: 'pending' },
      { id: 'ms-040', name: 'Full Go-Live', dueDate: '2025-12-15', status: 'pending' },
    ],
    budgetItems: [
      { id: 'bi-029', resource: 'Rachel Kim', role: 'CRM Consultant', budgetedHours: 2000, actualHours: 0, remainingHours: 2000, costRate: 105, billingRate: 170 },
      { id: 'bi-030', resource: 'Carlos Mendez', role: 'Integration Lead', budgetedHours: 1600, actualHours: 0, remainingHours: 1600, costRate: 95, billingRate: 155 },
    ],
    timeEntries: [],
  },
];
